import type { EncryptedBlob } from '@shared/types';
import { decryptWithMasterKey, encryptWithMasterKey } from '../crypto/keys';
import { openIndexedDb, requestToPromise, transactionDone } from '../utils/idb';

const DB_NAME = 'family-messenger-message-archive-keys';
const DB_VERSION = 1;
const STORE_NAME = 'archive_key_records';

export type MessageArchiveKeyRecord = {
  keyId: string;
  sourceCircleIdentityId: string;
  destinationId: string;
  destinationType: 'circle';
  rawKeyBase64: string;
  createdAt: string;
  updatedAt: string;
};

type StoredMessageArchiveKeyRecord = {
  keyId: string;
  sourceCircleIdentityId: string;
  destinationId: string;
  destinationType: 'circle';
  encryptedRecord: EncryptedBlob;
  updatedAt: string;
};

export function buildMessageArchiveKeyId(params: {
  sourceCircleIdentityId: string;
  destinationId: string;
}): string {
  return `${params.sourceCircleIdentityId.trim()}::${params.destinationId.trim()}`;
}

function openDb(): Promise<IDBDatabase> {
  return openIndexedDb(DB_NAME, DB_VERSION, (db) => {
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      const store = db.createObjectStore(STORE_NAME, { keyPath: 'keyId' });
      store.createIndex('bySourceCircle', 'sourceCircleIdentityId');
      store.createIndex('byDestination', 'destinationId');
    }
  });
}

export async function saveMessageArchiveKeyRecord(
  masterKey: CryptoKey,
  record: MessageArchiveKeyRecord
): Promise<void> {
  const next: MessageArchiveKeyRecord = {
    ...record,
    updatedAt: new Date().toISOString()
  };
  const encryptedRecord = await encryptWithMasterKey(masterKey, JSON.stringify(next));
  const stored: StoredMessageArchiveKeyRecord = {
    keyId: next.keyId,
    sourceCircleIdentityId: next.sourceCircleIdentityId,
    destinationId: next.destinationId,
    destinationType: next.destinationType,
    encryptedRecord,
    updatedAt: next.updatedAt
  };

  const db = await openDb();
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(stored);
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

export async function getMessageArchiveKeyRecord(
  masterKey: CryptoKey,
  keyId: string
): Promise<MessageArchiveKeyRecord | null> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const stored = await requestToPromise(tx.objectStore(STORE_NAME).get(keyId)) as StoredMessageArchiveKeyRecord | undefined;
    if (!stored) return null;
    const json = await decryptWithMasterKey(masterKey, stored.encryptedRecord);
    return JSON.parse(json) as MessageArchiveKeyRecord;
  } catch {
    return null;
  } finally {
    db.close();
  }
}

export async function listMessageArchiveKeyRecords(masterKey: CryptoKey): Promise<MessageArchiveKeyRecord[]> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const storedRecords = await requestToPromise(tx.objectStore(STORE_NAME).getAll()) as StoredMessageArchiveKeyRecord[];
    const records: MessageArchiveKeyRecord[] = [];
    for (const stored of storedRecords) {
      try {
        const json = await decryptWithMasterKey(masterKey, stored.encryptedRecord);
        records.push(JSON.parse(json) as MessageArchiveKeyRecord);
      } catch {
        // Ignore records from another profile key.
      }
    }
    return records;
  } finally {
    db.close();
  }
}

export async function deleteMessageArchiveKeyRecord(keyId: string): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(keyId);
    await transactionDone(tx);
  } finally {
    db.close();
  }
}

export async function deleteMessageArchiveKeyRecordsForIdentity(sourceCircleIdentityId: string): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const index = tx.objectStore(STORE_NAME).index('bySourceCircle');
    const rows = await requestToPromise(index.getAllKeys(sourceCircleIdentityId)) as IDBValidKey[];
    for (const key of rows) tx.objectStore(STORE_NAME).delete(key);
    await transactionDone(tx);
  } finally {
    db.close();
  }
}
